import { search } from '../../Foundation';

export class SearchBox {
  constructor(element) {
    this.base = element;
    this.trigger = element.querySelector('[data-search-box-trigger]');
    this.selectWrapper = element.querySelector('[data-search-box-select-wrapper]');
    this.dropdown = element.querySelector('[data-search-box-dropdown]');
    this.options = element.querySelectorAll('[data-search-box-option]');
    this.input = element.querySelector('[data-search-box-text-input]');
    this.hiddenInput = element.querySelector('[data-search-box-input]');
    this.valueSpan = element.querySelector('[data-search-box-value]');
    this.form = element.querySelector('form');
    this.submitButton = element.querySelector('[data-search-box-submit]');
    this.instanceId = SearchBox.nextId = (SearchBox.nextId || 0) + 1;

    // Running the search does not depend on the type selector, and the header
    // renders the box without a dropdown, so this is bound before the guard below.
    this._bindSubmitEvents();

    if (!this.trigger || !this.dropdown) return;

    this._configureAriaIds();
    this._bindEvents();
    this._setInputMode('pointer');
    this._close();
  }

  /**
   * Hands the phrase to Foundation/search, which builds the product listing URL
   * from the globals emitted next to the markup.
   */
  _submit() {
    const phrase = this.input ? this.input.value.trim() : '';

    if (!phrase) {
      if (this.input) this.input.focus();
      return;
    }

    search.execute({ phrase: phrase });
  }

  _bindSubmitEvents() {
    // In WebForms the box sits inside the ASP.NET server form, so there is no
    // dedicated form element here and Enter would otherwise cause a postback.
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this._submit();
      });
    }

    if (this.submitButton) {
      this.submitButton.addEventListener('click', (e) => {
        e.preventDefault();
        this._submit();
      });
    }

    if (this.input) {
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this._submit();
        }
      });
    }
  }

  _bindEvents() {
    this.trigger.addEventListener('click', () => this._toggle());
    this.trigger.addEventListener('keydown', (e) => this._onTriggerKeyDown(e));

    this.options.forEach((option) => {
      option.addEventListener('click', () => this._select(option));
      option.addEventListener('keydown', (e) => this._onOptionKeyDown(e, option));
    });

    // Close when clicking outside the component
    document.addEventListener('mousedown', (e) => {
      if (!this.base.contains(e.target)) {
        this._close();
      }
    });

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Tab' || e.key === 'Enter' || e.key.startsWith('Arrow')) {
          this._setInputMode('keyboard');
        }
      },
      true,
    );

    document.addEventListener('mousedown', () => this._setInputMode('pointer'), true);
    document.addEventListener('touchstart', () => this._setInputMode('pointer'), true);

    window.addEventListener('resize', () => {
      if (this.base.classList.contains('is-active')) {
        this._positionDropdown();
      }
    });
  }

  _configureAriaIds() {
    const triggerId = `search-type-trigger-${this.instanceId}`;
    const listboxId = `search-type-listbox-${this.instanceId}`;

    this.trigger.setAttribute('id', triggerId);
    this.trigger.setAttribute('aria-controls', listboxId);

    this.dropdown.setAttribute('id', listboxId);
    this.dropdown.setAttribute('aria-labelledby', triggerId);
  }

  _getOptions() {
    return Array.from(this.options);
  }

  _getSelectedOptionIndex() {
    const opts = this._getOptions();
    const selected = this.dropdown.querySelector('[aria-selected="true"]');
    const selectedIndex = opts.indexOf(selected);

    return selectedIndex >= 0 ? selectedIndex : 0;
  }

  _focusOptionAt(index) {
    const opts = this._getOptions();
    const clamped = Math.max(0, Math.min(index, opts.length - 1));
    const option = opts[clamped];

    if (option) {
      option.focus();
    }
  }

  _toggle() {
    this.base.classList.contains('is-active') ? this._close() : this._open('selected');
  }

  _setInputMode(mode) {
    this.base.setAttribute('data-search-box-input', mode);
  }

  _open(focusTarget = 'selected') {
    if (this.base.classList.contains('is-active')) {
      return;
    }

    this.base.classList.add('is-active');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.dropdown.hidden = false;
    this._positionDropdown();

    if (focusTarget === 'first') {
      this._focusOptionAt(0);
      return;
    }

    if (focusTarget === 'last') {
      this._focusOptionAt(this._getOptions().length - 1);
      return;
    }

    this._focusOptionAt(this._getSelectedOptionIndex());
  }

  _close(shouldFocusTrigger = false) {
    this.base.classList.remove('is-active');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.dropdown.hidden = true;
    this.dropdown.style.left = '0';
    this.dropdown.style.right = 'auto';

    if (shouldFocusTrigger) {
      this.trigger.focus();
    }
  }

  _select(option) {
    this.options.forEach((o) => o.setAttribute('aria-selected', 'false'));
    option.setAttribute('aria-selected', 'true');
    this.hiddenInput.value = option.dataset.value;
    this.valueSpan.textContent = option.textContent.trim();
    this._close(true);
  }

  _positionDropdown() {
    const wrapper = this.selectWrapper || this.trigger;
    if (!wrapper) {
      return;
    }

    const baseRect = this.base.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    let left = wrapperRect.left - baseRect.left;
    const width = Math.round(wrapperRect.width);

    this.dropdown.style.left = `${Math.round(left)}px`;
    this.dropdown.style.right = 'auto';
    this.dropdown.style.transform = 'none';
    this.dropdown.style.width = `${width}px`;
    this.dropdown.style.minWidth = `${width}px`;
    this.dropdown.style.maxWidth = `${width}px`;

    const rect = this.dropdown.getBoundingClientRect();
    if (rect.right > viewportWidth) {
      left = Math.max(0, left - (rect.right - viewportWidth));
      this.dropdown.style.left = `${Math.round(left)}px`;
    }

    const clampedRect = this.dropdown.getBoundingClientRect();
    if (clampedRect.left < 0) {
      this.dropdown.style.left = `${Math.round(left - clampedRect.left)}px`;
    }
  }

  _onTriggerKeyDown(e) {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this._open('selected');
        break;
      case 'ArrowDown':
        e.preventDefault();
        this._open('first');
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._open('last');
        break;
      case 'Escape':
        this._close();
        break;
    }
  }

  _onOptionKeyDown(e, option) {
    const opts = this._getOptions();
    const idx = opts.indexOf(option);
    const nextIdx = (idx + 1) % opts.length;
    const prevIdx = (idx - 1 + opts.length) % opts.length;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this._focusOptionAt(nextIdx);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._focusOptionAt(prevIdx);
        break;
      case 'Home':
        e.preventDefault();
        this._focusOptionAt(0);
        break;
      case 'End':
        e.preventDefault();
        this._focusOptionAt(opts.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        this._select(option);
        break;
      case 'Escape':
        e.preventDefault();
        this._close(true);
        break;
      case 'Tab':
        e.preventDefault();
        this._focusOptionAt(e.shiftKey ? prevIdx : nextIdx);
        break;
    }
  }
}
